import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link2, MapPin } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { loadGoogleMaps, onMapsAuthFailure } from '@/lib/googleMaps'
import { useMapsConfig } from '@/hooks/useMapsConfig'
import { toastHelpers } from '@/lib/toast-helpers'

// Drop pin for a counter / phone delivery order (F-DLV-N3). Paste the
// WhatsApp location or Google Maps link the customer sent, or tap the map.
// Without maps switched on the link (or "lat, lng") is the only way — still enough
// for the rider's Navigate button to go to the exact door.

export type Pin = { lat: number; lng: number }

const FALLBACK_CENTER = { lat: 31.5204, lng: 74.3587 }

export function PinDialog({
  orderId,
  orderNumber,
  address,
  initial,
  suggested,
  open,
  onClose,
  onSaved,
}: {
  orderId: string
  orderNumber: string
  address: string | null
  initial: Pin | null
  suggested?: (Pin & { source: 'previous_order' | 'rider_gps' }) | null
  open: boolean
  onClose: () => void
  /** Called after the pin is saved (null = removed), before onClose. */
  onSaved?: (pin: Pin | null) => void
}) {
  const qc = useQueryClient()
  const [pin, setPin] = useState<Pin | null>(initial)
  const [link, setLink] = useState('')
  const [mapError, setMapError] = useState<string | null>(null)
  const divRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)

  // Bhookly-issued key (F-BE-N90).
  const { data: maps } = useMapsConfig(open)
  const apiKey = maps?.browser_key ?? ''

  // Snapshot what the dialog opened with: the board refreshes underneath every
  // 15s with fresh objects, which must not reset a pin being placed.
  const seed = useRef<{ initial: Pin | null; suggested: Pin | null }>({ initial: null, suggested: null })
  useEffect(() => {
    if (open) {
      seed.current = { initial, suggested: suggested ?? null }
      setPin(initial)
      setLink('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open edge only
  }, [open])

  // The map mounts with the dialog; tap or drag to place the pin.
  useEffect(() => {
    if (!open || !apiKey) return
    let cancelled = false
    const off = onMapsAuthFailure(() => setMapError('The map isn’t loading right now — paste a link instead.'))
    // Wait a frame so the dialog's map div exists.
    const t = window.setTimeout(() => {
      loadGoogleMaps(apiKey)
        .then((maps) => {
          if (cancelled || !divRef.current) return
          const { initial: first, suggested: hint } = seed.current
          const map = new maps.Map(divRef.current, {
            center: first ?? hint ?? FALLBACK_CENTER,
            zoom: first || hint ? 17 : 12,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            gestureHandling: 'greedy',
          })
          mapRef.current = map
          map.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (e.latLng) setPin({ lat: e.latLng.lat(), lng: e.latLng.lng() })
          })
          // Draw the pin the dialog opened with.
          setPin((p) => (p ? { ...p } : p))
        })
        .catch((e: Error) => setMapError(e.message))
    }, 50)
    return () => {
      cancelled = true
      window.clearTimeout(t)
      off()
      markerRef.current?.setMap(null)
      markerRef.current = null
      mapRef.current = null
    }
  }, [open, apiKey])

  // Keep the marker on the pin.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!pin) {
      markerRef.current?.setMap(null)
      markerRef.current = null
      return
    }
    if (!markerRef.current) {
      markerRef.current = new google.maps.Marker({ map, position: pin, draggable: true })
      markerRef.current.addListener('dragend', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) setPin({ lat: e.latLng.lat(), lng: e.latLng.lng() })
      })
    } else {
      markerRef.current.setPosition(pin)
    }
    map.panTo(pin)
    if ((map.getZoom() ?? 0) < 16) map.setZoom(17)
  }, [pin])

  const resolve = useMutation({
    mutationFn: () => apiClient.resolveMapLink(link.trim()),
    onSuccess: (res) => {
      if (res.data) setPin({ lat: res.data.lat, lng: res.data.lng })
    },
    onError: (err: unknown) =>
      toastHelpers.error('No location in that link', (err as { message?: string })?.message || 'Try another link.'),
  })

  const save = useMutation({
    mutationFn: (p: Pin | null) => apiClient.setOrderPin(orderId, p),
    onSuccess: (_d, p) => {
      toastHelpers.success(p ? 'Pin saved' : 'Pin removed', `Order #${orderNumber}`)
      qc.invalidateQueries({ queryKey: ['dispatch'] })
      onSaved?.(p)
      onClose()
    },
    onError: (err: unknown) =>
      toastHelpers.error('Could not save the pin', (err as { message?: string })?.message || 'Please try again.'),
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Drop pin · #{orderNumber}</DialogTitle>
          <DialogDescription>{address || 'No address typed on the order'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {suggested && !pin && (
            <div className="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm">
              <span>
                {suggested.source === 'previous_order'
                  ? 'This customer’s last delivery to this address had a pin.'
                  : 'A rider delivered to this address before — use where they were?'}
              </span>
              <Button size="sm" variant="outline" onClick={() => setPin({ lat: suggested.lat, lng: suggested.lng })}>
                Use it
              </Button>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="pin-link">Paste the location the customer sent</Label>
            <div className="flex gap-2">
              <Input
                id="pin-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="WhatsApp location, Google Maps link, or 31.52, 74.35"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && link.trim()) resolve.mutate()
                }}
              />
              <Button variant="outline" onClick={() => resolve.mutate()} disabled={!link.trim() || resolve.isPending}>
                <Link2 className="mr-1.5 h-4 w-4" aria-hidden /> Find
              </Button>
            </div>
          </div>

          {apiKey ? (
            <>
              {mapError && <p className="text-xs text-red-700 dark:text-red-400">{mapError}</p>}
              <div ref={divRef} className="h-72 w-full overflow-hidden rounded-lg border" />
              <p className="text-xs text-muted-foreground">Tap the map to drop the pin, drag it to adjust.</p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Maps aren’t switched on for your restaurant yet. Pasted links still work — message Bhookly support to get the map.
            </p>
          )}

          {pin && (
            <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
              <a
                className="underline"
                href={`https://www.google.com/maps/search/?api=1&query=${pin.lat},${pin.lng}`}
                target="_blank"
                rel="noreferrer"
              >
                check on Google Maps
              </a>
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {initial ? (
            <Button variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending}>
              Remove pin
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => pin && save.mutate(pin)} disabled={!pin || save.isPending}>
              Save pin
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
